// PASTE THIS ENTIRE FILE INTO src/components/ConversationDrillDownDialog.jsx
// Replace whatever is currently in that file. Do not mix with any other code.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider,
  IconButton, Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

const USER_COLOR = "#378ADD";
const CARA_COLOR = "#1D9E75";

function fmtDateTime(val) {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d)) return "";
  return d.toLocaleString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function timeMs(val) {
  if (!val) return 0;
  const t = new Date(val).getTime();
  return isNaN(t) ? 0 : t;
}

// Pull the badge label off a row regardless of which insight module sent it.
// Backends use Emotion / Polarity / Score / Willing as the field name,
// depending on which compliance category they query.
function extractBadge(row) {
  return row.Emotion || row.Polarity || row.Score || row.Willing || row.AbandonStage
      || row.emotion || row.polarity || row.score || row.willing || row.abandonStage
      || null;
}

// The opening "Hi" is written by the platform, not typed by the customer, so
// it's noise in a transcript. Matches the same predicate /gethiresponses uses
// (userresponse = 'Hi' AND iniconv = 0). If iniconv isn't in the SELECT list
// we fall back to the text match alone — a customer who genuinely replies
// "Hi" mid-conversation is indistinguishable without that column.
function isInsertedHi(row) {
  const txt = (row.userresponse || "").trim();
  if (!/^hi[.!]?$/i.test(txt)) return false;
  const ini = row.iniconv ?? row.IniConv ?? row.Iniconv;
  return ini == null ? true : Number(ini) === 0;
}

// Collapse rows into one group per ConversationID15 (or CustomerID when conv
// id is missing). Each row contributes up to two messages, and every message
// in a group is then sorted strictly newest-first on its OWN timestamp —
// messages are never welded into pairs, because a row's Cara reply can land
// between the next row's user message and its reply, which makes any
// pair-based ordering read backwards. Since Cara always answers after the
// customer, descending order puts each Cara message directly above the
// message it responds to.
function groupConversations(rows, isEmail) {
  const groups = new Map();

  rows.forEach((row, i) => {
    const customerId = row.CustomerID || row.customerid;
    const key =
      row.ConversationID15
        ? `conv:${row.ConversationID15}`
        : customerId
        ? `cust:${customerId}`
        : `row:${i}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        customerName:   row.CustomerName || row.customername || "—",
        customerId,
        conversationId: row.ConversationID15,
        emotion:        extractBadge(row),
        messages: [],
      });
    }
    const g = groups.get(key);
    // pick up the badge if it appeared on a later row but not the first
    if (!g.emotion) {
      const b = extractBadge(row);
      if (b) g.emotion = b;
    }

    if (row.cararesponse || (isEmail && row.cararesponsesubject)) {
      g.messages.push({
        _idx:    i,
        time:    row.cararesponsetime,
        who:     "CARA",
        color:   CARA_COLOR,
        message: row.cararesponse,
        subject: isEmail ? row.cararesponsesubject : null,
      });
    }

    const hasUserText = row.userresponse || (isEmail && row.userresponsesubject);
    if (hasUserText && !isInsertedHi(row)) {
      g.messages.push({
        _idx:    i,
        time:    row.userresponsetime,
        who:     "USER",
        color:   USER_COLOR,
        message: row.userresponse,
        subject: isEmail ? row.userresponsesubject : null,
      });
    }
  });

  groups.forEach(g => {
    g.messages.sort((a, b) => {
      const d = timeMs(b.time) - timeMs(a.time);
      if (d) return d;
      // Same timestamp to the second: keep Cara above the reply it answers,
      // then fall back to reverse row order so the result is stable.
      if (a.who !== b.who) return a.who === "CARA" ? -1 : 1;
      return b._idx - a._idx;
    });
    g.lastTime  = g.messages.length ? timeMs(g.messages[0].time) : 0;
    g.userCount = g.messages.filter(m => m.who === "USER").length;
    g.caraCount = g.messages.filter(m => m.who === "CARA").length;
  });

  return Array.from(groups.values())
    .filter(g => g.messages.length > 0)
    .sort((a, b) => b.lastTime - a.lastTime);
}

function MessageRow({ msg }) {
  if (!msg || (!msg.message && !msg.subject)) return null;
  return (
    <Box sx={{ display: "flex", gap: 1.5, py: 0.75, alignItems: "flex-start" }}>
      <Box sx={{
        minWidth: 138, fontSize: 11, color: "text.secondary", pt: 0.25,
        fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
      }}>
        {fmtDateTime(msg.time)}
      </Box>
      <Box sx={{
        minWidth: 50, fontSize: 11, fontWeight: 600, pt: 0.25,
        color: msg.color, textTransform: "uppercase", letterSpacing: "0.04em",
      }}>
        {msg.who}
      </Box>
      <Box sx={{ flex: 1, fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
        {msg.subject && (
          <Box sx={{ fontWeight: 500, mb: 0.25, color: "text.secondary" }}>
            Subject: {msg.subject}
          </Box>
        )}
        {msg.message}
      </Box>
    </Box>
  );
}

function ConversationGroupCard({ group, emotionColors = {} }) {
  const emotionColor = group.emotion ? emotionColors[group.emotion] : null;

  return (
    <Box sx={{
      border: "0.5px solid", borderColor: "divider",
      borderRadius: 2, p: 1.5, mb: 1.5,
      transition: "border-color .15s",
      "&:hover": { borderColor: "rgba(55,138,221,0.4)" },
    }}>
      {/* Group header */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1, mb: 1,
        pb: 1, borderBottom: "0.5px solid", borderColor: "divider",
        flexWrap: "wrap",
      }}>
        <PersonOutlineIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        <Box sx={{ fontSize: 13, fontWeight: 500 }}>{group.customerName}</Box>
        {group.customerId && (
          <Chip label={`ID: ${group.customerId}`} size="small"
            sx={{ height: 20, fontSize: 11 }} />
        )}
        {group.conversationId && (
          <Chip label={`Conv: ${group.conversationId}`} size="small" variant="outlined"
            sx={{ height: 20, fontSize: 11 }} />
        )}
        {group.emotion && (
          <Chip
            label={group.emotion}
            size="small"
            sx={{
              height: 20, fontSize: 11, fontWeight: 500,
              maxWidth: 240,
              background: emotionColor || "rgba(0,0,0,0.08)",
              color:      emotionColor ? "#fff" : "text.primary",
              "& .MuiChip-label": {
                overflow: "hidden", textOverflow: "ellipsis",
              },
            }}
            title={group.emotion}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Box sx={{ fontSize: 11, color: "text.secondary" }}>
          {group.userCount} user · {group.caraCount} cara
        </Box>
      </Box>

      {/* Transcript — strictly newest first */}
      {group.messages.map((msg, i) => (
        <MessageRow key={i} msg={msg} />
      ))}
    </Box>
  );
}

const ConversationDrillDownDialog = ({
  open,
  onClose,
  title = "Conversation details",
  metric,                // { label, value, color }
  context = [],          // [{ label, value }, ...]
  channel = "Texts",     // "Texts" | "Emails"
  fetchData,             // async () => row[]
  emotionColors = {},    // optional { [label]: color } for per-group badge chips
  emptyMessage = "No conversations found for this selection.",
}) => {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const isEmail = channel === "Emails";

  const load = useCallback(async () => {
    if (!fetchData) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetchData();
      setData(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error(e);
      setError("Failed to load conversation details.");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [fetchData]);

  useEffect(() => {
    if (open) {
      load();
    } else {
      setData([]);
      setError("");
    }
  }, [open, load]);

  const groups = useMemo(() => groupConversations(data, isEmail), [data, isEmail]);

  const totalMessages = useMemo(
    () => groups.reduce((sum, g) => sum + g.messages.length, 0),
    [groups]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { height: "85vh" } }}
    >
      <DialogTitle sx={{
        display: "flex", justifyContent: "space-between",
        alignItems: "flex-start", pr: 1, pb: 1.25,
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
            {title}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
            {metric && (
              <Chip
                label={`${metric.label}: ${metric.value}`}
                size="small"
                sx={{
                  height: 22, fontSize: 11, fontWeight: 500,
                  background: metric.color || USER_COLOR,
                  color: "#fff",
                }}
              />
            )}
            {context.map((c, i) => (
              <Chip key={i}
                label={`${c.label}: ${c.value}`}
                size="small" variant="outlined"
                sx={{ height: 22, fontSize: 11 }}
              />
            ))}
            <Chip label={channel} size="small" variant="outlined"
              sx={{ height: 22, fontSize: 11 }} />
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading && (
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
            <CircularProgress size={20} /> Loading conversations…
          </Box>
        )}

        {!loading && !error && groups.length === 0 && (
          <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
            {emptyMessage}
          </Typography>
        )}

        {!loading && groups.length > 0 && (
          <>
            <Typography sx={{
              fontSize: 11, color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5,
            }}>
              {groups.length} conversation{groups.length === 1 ? "" : "s"}
              {" · "}
              {totalMessages} message{totalMessages === 1 ? "" : "s"}
              {" · newest first"}
            </Typography>
            {groups.map(g => (
              <ConversationGroupCard key={g.key} group={g} emotionColors={emotionColors} />
            ))}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: "0.5px solid", borderColor: "divider", py: 1 }}>
        <Button onClick={onClose} size="small">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConversationDrillDownDialog;